import { useState, FC } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Divider } from 'antd';
import { format } from 'date-fns';
import { twJoin } from '@utils/twMerge';
import { FileIcon } from '@dynamika-shared/ui/icons';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Dot } from '@dynamika-shared/ui/Dot/Dot';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { VSParticipantFormEdit } from '@components/VSParticipantFormEdit/VSParticipantFormEdit';
import { VSParticipantFormView } from '@dynamika-shared/components/VSParticipantFormView/VSParticipantFormView';
import { ValidationSpecialistUpload } from '@restore/ValidationSpecialistSubjects/ValidationSpecialistSubjects.types';
import { isVSSchema } from '@utils/validationSpecialist';
import { UploadFormData } from '@dynamika-shared/types/validationSpecialist';
import { useVisitUploads } from '../../SubjectDetails/hooks/useVisitUploads';

interface UploadCardProps {
  uploadFormSchema?: UploadFormData;
  upload: ValidationSpecialistUpload;
}

type FormMode = 'closed' | 'view' | 'edit';

export const UploadCard: FC<UploadCardProps> = ({ uploadFormSchema, upload }) => {
  const { clinicalTrialId } = useParams<{ clinicalTrialId: string }>();
  const { handleDownloadFile } = useVisitUploads({ clinicalTrialId });

  const {
    id,
    siteUserFullName,
    uploadDate,
    status,
    files,
    validationSpecialistQuestionnaireId,
    validationSpecialistQuestionnaireResponse
  } = upload;

  const [formMode, setFormMode] = useState<FormMode>('closed');
  const [openDropdown, setOpenDropdown] = useState(false);

  const isCompleted = status === 'COMPLETE' || !!validationSpecialistQuestionnaireId;

  const label = isCompleted ? 'View Participant Form' : 'Fill Participant Form';

  // A form can only be opened when a Validation Specialist schema is available for this upload.
  const hasVSSchema = isVSSchema(uploadFormSchema);

  const sortedFiles = [...files].sort(a => (a.tag === 'initial' ? -1 : 1));

  return (
    <>
      <Card key={id} className='pl-1 pr-2 !mb-3 !mt-0 rounded-[16px]'>
        <div className='mb-[12px] flex items-center justify-between font-medium rounded-[8px] bg-[#F6F9FF] px-[12px] border-[1px] border-brightGray'>
          <div className='flex items-center text-1624 font-medium'>
            <div>
              <span className='text-dimGray'>Uploaded by</span>
              <span> {siteUserFullName} </span>
            </div>
              <Dot className='!mx-0.5 text-[24px]' />
            <div
              className='text-dimGray whitespace-nowrap'
            >
              <span className='text-1624'>
                {format(new Date(uploadDate), 'dd-MMM-yyyy')}
              </span>{' '}
              <span className='text-1624'>{format(new Date(uploadDate), 'HH:mm:ss')}</span>
            </div>
          </div>

        <Button
          className="!text-1424 !pr-0"
          type="link"
          title={label}
          disabled={!hasVSSchema}
          onClick={() => setFormMode(isCompleted ? 'view' : 'edit')}
        >
          {label}
        </Button>
        </div>
        <div className='flex items-center justify-between last:mb-0'>
          <div className='flex text-dimGray ml-[12px] text-1424'>
            <div className='flex items-center font-medium gap-1'>
              File format
            </div>
          </div>
          <div
            className='flex text-1424 font-medium items-center gap-2 justify-between mr-[12px]'
          >
            <span className='text-spanishGray'>Action</span>
          </div>
        </div>
        <Divider className='my-1.5' />

        {sortedFiles.map(({ id, name, tag }) => {
          const isInitial = tag === 'initial';
          const show = isInitial || (openDropdown && !isInitial);

          if (!show) return null;

          return (
            <div
              key={id}
              className="transition-all duration-500 ease-in-out flex items-center justify-between mb-2 last:mb-0"
            >
              <div className="flex items-center text-dimGray">
                {isInitial && (
                  <>
                    <ChevronIcon
                      onClick={() => setOpenDropdown(!openDropdown)}
                      className={twJoin(
                        'b-1 h-3 w-3 text-spanishGray cursor-pointer rounded-full border-brightGray border bg-ghostWhite',
                        openDropdown && '!bg-waterLight !text-black'
                      )}
                      classNameButton='h-3 w-3'
                      dir={openDropdown ? 'up' : 'down'}
                    />
                    <FileIcon fontSize={24} className='mx-[10px]' />
                  </>
                )}

                <span
                  className={twJoin(
                    'text-1624 text-black',
                    !isInitial && 'ml-[40px]'
                  )}
                >
                  {name}
                </span>
              </div>

              <Button
                className='mr-[12px] !p-0 !text-1424 [&>span]:!text-vividTangelo [&>span:hover]:!text-vividTangelo/80'
                title='Download'
                type='link'
                onClick={() => handleDownloadFile(id)}
              >
                Download
              </Button>
            </div>
          );
        })}

      </Card>
      {uploadFormSchema && (
        <VSParticipantFormView
          edtfData={validationSpecialistQuestionnaireResponse}
          uploadFormSchema={uploadFormSchema}
          uploadDateTime={uploadDate}
          isOpen={formMode === 'view'}
          onClose={() => setFormMode('closed')}
          siteUserName={siteUserFullName}
        />
      )}
      {formMode === 'edit' &&
        <VSParticipantFormEdit
          uploadItemId={id}
          setFormMode={setFormMode}
        />
      }
    </>
  );
};
